/**
 * Shared horizontal-resize gesture for the board's spanning strips (accommodation nights + services days).
 *
 * On mousedown at a cell's right edge, we track the pointer and snap the new span to whichever day-head
 * boundary is nearest the cursor, calling `onResize` whenever the snapped span changes. The two strips
 * differ only in which boundary marks a span's end (a night ends at day-head `day+span`; a day-service
 * ends at day-head `day+span-1`), so callers pass an `edgeOffset` (0 for nights, -1 for services) plus
 * the already-computed `maxSpan`.
 */
import { useCallback } from 'react';

import type { BoardPart } from '../model';
import { headRightsOf } from './stripGeometry';

export function useStripResize(
  rowRef: React.RefObject<HTMLDivElement | null>,
  onResize: (uid: string, span: number) => void
) {
  return useCallback(
    (event: React.MouseEvent, part: BoardPart, maxSpan: number, edgeOffset: number) => {
      event.preventDefault();
      event.stopPropagation();
      const rights = headRightsOf(rowRef);
      let lastSpan = part.span;
      const onMouseMove = (ev: MouseEvent) => {
        let best = 1;
        let bestDist = Infinity;
        for (let s = 1; s <= maxSpan; s += 1) {
          const x = rights[part.day + s + edgeOffset] ?? rights[rights.length - 1];
          const dist = Math.abs(x - ev.clientX);
          if (dist < bestDist) {
            bestDist = dist;
            best = s;
          }
        }
        if (best !== lastSpan) {
          lastSpan = best;
          onResize(part.uid, best);
        }
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onUp);
    },
    [rowRef, onResize]
  );
}
